import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { BoutonPrincipal, BoutonSecondaire, MarqueObligatoire, MessageErreur } from '../ui/Primitives';
import { ChampRecherchable } from '../ui/ChampRecherchable';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { aujourdhui } from '../../lib/dates-civiles';
import {
  LIMITES_RECLAMATION,
  useReclamants,
  useSaisirReclamation,
  type OptionsRegistre,
  type QualiteReclamant,
  type SaisieEquipe,
} from '../../lib/useReclamations';

const CLASSE_LIBELLE = 'block text-[11px] font-bold uppercase tracking-wide text-codialis-navy';
const CLASSE_CHAMP =
  'mt-1 min-h-[44px] w-full min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-codialis-navy';

type QualiteRattachee = 'APPRENANT' | 'FORMATEUR' | 'ENTREPRISE';

function rattachee(qualite: QualiteReclamant): qualite is QualiteRattachee {
  return qualite === 'APPRENANT' || qualite === 'FORMATEUR' || qualite === 'ENTREPRISE';
}

const LIBELLES_FICHE: Record<QualiteRattachee, { libelle: string; placeholder: string }> = {
  APPRENANT: { libelle: 'Dossier de l’apprenant(e)', placeholder: 'Nom ou prénom…' },
  FORMATEUR: { libelle: 'Formateur', placeholder: 'Nom ou prénom…' },
  ENTREPRISE: { libelle: 'Entreprise', placeholder: 'Raison sociale…' },
};

function Champ({ id, libelle, obligatoire = false, children }: { id: string; libelle: string; obligatoire?: boolean; children: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className={CLASSE_LIBELLE}>
        {libelle}
        {obligatoire && <MarqueObligatoire />}
      </label>
      {children}
    </div>
  );
}

/**
 * Saisie d'une réclamation reçue par mail, téléphone, courrier ou en personne
 * (TF-482). Elle est rattachée à la personne qui réclame — son dossier, sa
 * fiche, son entreprise, sinon son nom — et l'équipe confirme l'avoir informée
 * que sa réclamation est tracée. Sans responsable choisi, l'API l'assigne au
 * responsable des réclamations de l'antenne.
 */
export function SaisieReclamation({
  options,
  siteParDefaut,
  onFermer,
  onEnregistree,
}: {
  options: OptionsRegistre;
  siteParDefaut: string | null;
  onFermer: () => void;
  onEnregistree: (id: string) => void;
}): JSX.Element {
  const id = useId();
  const toast = useToast();
  const saisir = useSaisirReclamation();
  const [qualite, setQualite] = useState<QualiteReclamant>('APPRENANT');
  const [fiche, setFiche] = useState<string | null>(null);
  const [saisie, setSaisie] = useState({
    site: siteParDefaut ?? (options.sites.length === 1 ? (options.sites[0]?.code ?? '') : ''),
    nomReclamant: '',
    contact: '',
    formationId: '',
    canal: '',
    recueLe: aujourdhui(),
    objet: '',
    description: '',
    responsableId: '',
    reclamantInforme: false,
  });
  const reclamants = useReclamants(rattachee(qualite) ? qualite : null, '');

  const changer = <C extends keyof typeof saisie>(cle: C, valeur: (typeof saisie)[C]): void =>
    setSaisie((avant) => ({ ...avant, [cle]: valeur }));
  const choisirQualite = (valeur: QualiteReclamant): void => {
    setQualite(valeur);
    setFiche(null);
  };

  // L'antenne d'un(e) apprenant(e) est celle de son dossier.
  const avecAntenne = qualite !== 'APPRENANT';
  const avecNom = qualite === 'ENTREPRISE' || qualite === 'FINANCEUR' || qualite === 'AUTRE';
  const responsables = saisie.site ? (options.responsables[saisie.site] ?? []) : [];
  const valide =
    saisie.canal !== '' &&
    saisie.recueLe !== '' &&
    saisie.objet.trim().length >= LIMITES_RECLAMATION.objet.min &&
    saisie.description.trim().length >= LIMITES_RECLAMATION.description.min &&
    saisie.reclamantInforme &&
    (!rattachee(qualite) || fiche !== null) &&
    (!avecNom || saisie.nomReclamant.trim().length >= LIMITES_RECLAMATION.nom.min) &&
    (!avecAntenne || saisie.site !== '');

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (!valide) return;
    const corps: SaisieEquipe = {
      qualite,
      canal: saisie.canal,
      recueLe: saisie.recueLe,
      objet: saisie.objet.trim(),
      description: saisie.description.trim(),
      reclamantInforme: true,
      ...(qualite === 'APPRENANT' && fiche ? { apprenantId: fiche } : {}),
      ...(qualite === 'FORMATEUR' && fiche ? { formateurId: fiche } : {}),
      ...(qualite === 'ENTREPRISE' && fiche ? { entrepriseId: fiche } : {}),
      ...(avecAntenne ? { site: saisie.site } : {}),
      ...(avecNom ? { nomReclamant: saisie.nomReclamant.trim() } : {}),
      ...(saisie.contact.trim() ? { contact: saisie.contact.trim() } : {}),
      ...(qualite !== 'APPRENANT' && saisie.formationId ? { formationId: saisie.formationId } : {}),
      ...(avecAntenne && saisie.responsableId ? { responsableId: saisie.responsableId } : {}),
    };
    saisir.mutate(corps, {
      onSuccess: (creee) => {
        toast(`Réclamation ${creee.reference} enregistrée`);
        onEnregistree(creee.id);
      },
    });
  };

  return (
    <Modale titre="Saisir une réclamation" sousTitre="Reçue par mail, téléphone, courrier ou en personne" ouverte onFermer={onFermer} largeur="max-w-3xl">
      <form onSubmit={envoyer} className="space-y-4 p-4 sm:p-6">
        <fieldset className="space-y-3">
          <legend className="font-display text-sm font-bold text-codialis-navy">Qui réclame</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Champ id={`${id}-qualite`} libelle="Qualité" obligatoire>
              <select
                id={`${id}-qualite`}
                value={qualite}
                onChange={(evenement) => choisirQualite(evenement.target.value as QualiteReclamant)}
                className={CLASSE_CHAMP}
              >
                {options.qualites.map((option) => (
                  <option key={option.valeur} value={option.valeur}>
                    {option.libelle}
                  </option>
                ))}
              </select>
            </Champ>
            {rattachee(qualite) && (
              <div className="min-w-0">
                <ChampRecherchable
                  key={qualite}
                  id={`${id}-fiche`}
                  libelle={`${LIBELLES_FICHE[qualite].libelle} (obligatoire)`}
                  classeLibelle={CLASSE_LIBELLE}
                  placeholder={reclamants.isLoading ? 'Chargement…' : LIBELLES_FICHE[qualite].placeholder}
                  options={(reclamants.data ?? []).map((ligne) => ({ id: ligne.id, libelle: ligne.libelle, detail: ligne.detail }))}
                  valeur={fiche}
                  onChoisir={setFiche}
                  aucunResultat="Aucune fiche de vos antennes ne correspond."
                />
              </div>
            )}
            {avecNom && (
              <Champ id={`${id}-nom`} libelle={qualite === 'ENTREPRISE' ? 'Personne qui réclame pour l’entreprise' : 'Nom de la personne'} obligatoire>
                <input
                  id={`${id}-nom`}
                  type="text"
                  required
                  maxLength={LIMITES_RECLAMATION.nom.max}
                  value={saisie.nomReclamant}
                  onChange={(evenement) => changer('nomReclamant', evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </Champ>
            )}
            <Champ id={`${id}-contact`} libelle="E-mail ou téléphone">
              <input
                id={`${id}-contact`}
                type="text"
                maxLength={LIMITES_RECLAMATION.contact}
                value={saisie.contact}
                onChange={(evenement) => changer('contact', evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </Champ>
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="font-display text-sm font-bold text-codialis-navy">Réclamation</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {avecAntenne && (
              <Champ id={`${id}-site`} libelle="Antenne" obligatoire>
                <select
                  id={`${id}-site`}
                  required
                  value={saisie.site}
                  onChange={(evenement) => setSaisie((avant) => ({ ...avant, site: evenement.target.value, responsableId: '' }))}
                  className={CLASSE_CHAMP}
                >
                  <option value="">Choisir…</option>
                  {options.sites.map((site) => (
                    <option key={site.code} value={site.code}>
                      {site.nom} · {site.code}
                    </option>
                  ))}
                </select>
              </Champ>
            )}
            {qualite !== 'APPRENANT' && (
              <Champ id={`${id}-formation`} libelle="Formation concernée">
                <select
                  id={`${id}-formation`}
                  value={saisie.formationId}
                  onChange={(evenement) => changer('formationId', evenement.target.value)}
                  className={CLASSE_CHAMP}
                >
                  <option value="">Aucune en particulier</option>
                  {options.formations.map((formation) => (
                    <option key={formation.id} value={formation.id}>
                      {formation.titre}
                    </option>
                  ))}
                </select>
              </Champ>
            )}
            <Champ id={`${id}-canal`} libelle="Reçue par" obligatoire>
              <select
                id={`${id}-canal`}
                required
                value={saisie.canal}
                onChange={(evenement) => changer('canal', evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                <option value="">Choisir…</option>
                {options.canaux.map((option) => (
                  <option key={option.valeur} value={option.valeur}>
                    {option.libelle}
                  </option>
                ))}
              </select>
            </Champ>
            <Champ id={`${id}-recue`} libelle="Reçue le" obligatoire>
              <input
                id={`${id}-recue`}
                type="date"
                required
                max={aujourdhui()}
                value={saisie.recueLe}
                onChange={(evenement) => changer('recueLe', evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </Champ>
          </div>
          <Champ id={`${id}-objet`} libelle="Objet" obligatoire>
            <input
              id={`${id}-objet`}
              type="text"
              required
              maxLength={LIMITES_RECLAMATION.objet.max}
              value={saisie.objet}
              onChange={(evenement) => changer('objet', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ id={`${id}-description`} libelle="Ce que la personne réclame" obligatoire>
            <textarea
              id={`${id}-description`}
              required
              rows={5}
              maxLength={LIMITES_RECLAMATION.description.max}
              value={saisie.description}
              onChange={(evenement) => changer('description', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          {avecAntenne && saisie.site && (
            <Champ id={`${id}-responsable`} libelle="Responsable">
              <select
                id={`${id}-responsable`}
                value={saisie.responsableId}
                onChange={(evenement) => changer('responsableId', evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                <option value="">Responsable des réclamations de l’antenne</option>
                {responsables.map((candidat) => (
                  <option key={candidat.id} value={candidat.id}>
                    {candidat.nom} · {candidat.fonction}
                  </option>
                ))}
              </select>
            </Champ>
          )}
        </fieldset>

        <label className="flex items-start gap-3 rounded-xl border border-codialis-sky/30 bg-codialis-sky/5 px-3 py-3 text-sm text-codialis-navy">
          <input
            type="checkbox"
            required
            checked={saisie.reclamantInforme}
            onChange={(evenement) => changer('reclamantInforme', evenement.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0"
          />
          <span className="min-w-0">
            {options.mentionEquipe}
            <MarqueObligatoire />
          </span>
        </label>

        {saisir.error && (
          <MessageErreur
            message={saisir.error instanceof ErreurApi ? saisir.error.message : 'La réclamation n’a pas pu être enregistrée.'}
          />
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={!valide || saisir.isPending}>
            {saisir.isPending ? 'Enregistrement…' : 'Enregistrer la réclamation'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}
